import Variation from "./Variation"
import ArticleCollection from "./ArticleCollection"
import { TEMPORAL_BOV } from "./TemporalVariationSaved"

// The saved-line helpers live in TemporalVariationSaved (no imports, so BaseBOM can use them); re-exported here.
// Only named exports and no domain module imports this one, so the dist that professional-area reads gets it
// from the explicit include in tsconfig.docker.json, not from the generated index.ts.
export {
    TEMPORAL_BOV,
    getSavedTemporalAttributes,
    hasSavedTemporalAttributes,
    isSavedTemporalAttributeExpired,
    isTemporalBov,
} from "./TemporalVariationSaved"
export type { SavedTemporalAttribute } from "./TemporalVariationSaved"
import { i18nLabelInterface, variationType } from "./catalog"
import { VariationProps } from "./variation"

/**
 * Temporal variations (DV-6834): ERP variations with `BOV = 'T'` — special conditions of a model with a
 * validity window of days and a surcharge of their own. They never reach the public catalog (byFamily JSON,
 * /v2/articles/variations, Mongo articles/prices): a v3 endpoint sends them only to the users enabled by
 * toggle, and the frontends superpose them on the data they already have. These helpers are that superposition,
 * kept here so every surface merges and reads them the same way.
 *
 * The ERP validity is inclusive: a variation valid "until 23/1/2026" can still be ordered on 23/1. Base and
 * Variation compare `now < until`, so the end is moved to the start of the next day.
 */

export const TEMPORAL_OPTION_TYPE = 'temporal'

/**
 * The only toggle of the feature, targeted by email: who can choose, save and order temporal variations. Lines
 * already saved with one keep being read and priced with it whatever the toggle says (only they carry a `T`).
 */
export const TEMPORAL_VARIATIONS_TOGGLE = 'temporalVariations'

const DAY_MS = 24 * 60 * 60 * 1000

/** Pendant height ('Lon') is a cable limit of the model, not a choice: its temporal rows are left out. */
export const TEMPORAL_VARIATION_TYPES: variationType[] = ['color', 'temperature', 'control', 'installation']

export type TemporalOptionsKey = 'finishes' | 'temperatures' | 'controls' | 'installations'

const OPTIONS_KEY_BY_TYPE: Record<string, TemporalOptionsKey> = {
    color: 'finishes',
    temperature: 'temperatures',
    control: 'controls',
    installation: 'installations',
}


/** Inclusive ERP dates, ISO `yyyy-mm-dd`, for display ("Temporal until 23/01"). */
export interface TemporalValidity {
    from: string | null
    to: string | null
}

/** Display data of a finish that the ERP row does not carry (swatch, RAL, thumbnail). */
export interface TemporalFinishDisplay {
    description?: i18nLabelInterface
    familyName?: string
    ral?: string
    hex?: string
    rgb?: string
    thumbnailImage?: string
    thumbnailImageSquare?: string
}

/** An entry of `attributeOptions` (byFamily shape) for a temporal variation. */
export interface TemporalOptionEntry {
    modifier: string
    description: i18nLabelInterface
    type: typeof TEMPORAL_OPTION_TYPE
    MOQ: number
    /** d/m/yyyy, as the byFamily JSON stores it (Base.isAttributeCurrentlyValid reads it). */
    fromValidityDate: string
    /** d/m/yyyy of the day after the last valid day (Base compares `now < to`). */
    toValidityDate: string
    temporal: TemporalValidity
    familyName?: string
    ral?: string
    hex?: string
    rgb?: string
    thumbnailImage?: string
    thumbnailImageSquare?: string
    trackSystem?: boolean
}

/** What the v3 endpoint sends per temporal variation: the domain variation and its option entry. */
export interface TemporalVariationOffer {
    model: string
    optionsKey: TemporalOptionsKey
    variation: VariationProps
    option: TemporalOptionEntry
}


interface CollectionData {
    variations?: VariationProps[]
    [key: string]: unknown
}

type ModelData = Record<string, unknown> & { model?: string }

const pad = (value: number) => String(value).padStart(2, '0')

const toDayMonthYear = (timestamp: number) => {
    const date = new Date(timestamp)
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
}

const toIsoDay = (timestamp: number) => {
    const date = new Date(timestamp)
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const isValidTimestamp = (value: unknown): value is number => typeof value === 'number' && !Number.isNaN(value)

/** Same key the catalog uses to recognise a variation inside a reference. */
const variationKey = (model: string, type: string, modifier: string) => `${model}|${type}|${modifier}`

const keyOfProps = (props: VariationProps) =>
    variationKey(String(props.model), props.type, String(new Variation(props).getModifier()))


/**
 * The domain variation of an ERP row with `BOV = 'T'`, with its last day included. Null for any other row
 * and for the pendant-height ('Lon') ones.
 */
export const temporalVariationFromErp = (row: Record<string, unknown>, finishMasters?: Array<{ id: number; code: number }>): Variation | null => {
    if (String(row?.BOV ?? '').trim() !== TEMPORAL_BOV) return null
    const variation = Variation.fromERP(row, finishMasters)
    if (!TEMPORAL_VARIATION_TYPES.includes(variation.getType())) return null

    const props = variation.getProps()
    const until = props.availability?.until
    if (isValidTimestamp(until)) {
        props.availability = { ...props.availability, until: until + DAY_MS }
    }
    return new Variation(props)
}

/** Whether a temporal variation can be chosen or ordered at `now`. */
export const isTemporalVariationValidAt = (variation: Variation, now: number) => {
    const availability = variation.getProps().availability ?? { since: null }
    const startsBeforeNow = !isValidTimestamp(availability.since) || now >= availability.since
    const endsAfterNow = !isValidTimestamp(availability.until) || now < availability.until
    return startsBeforeNow && endsAfterNow
}

const temporalValidity = (variation: Variation): TemporalValidity => {
    const availability = variation.getProps().availability ?? { since: null }
    let from: string | null = null
    let to: string | null = null
    if (isValidTimestamp(availability.since)) from = toIsoDay(availability.since)
    if (isValidTimestamp(availability.until)) to = toIsoDay(availability.until - DAY_MS)
    return { from, to }
}

/** The same description with every language transformed. */
const mapLanguages = (description: i18nLabelInterface, transform: (text: string) => string): i18nLabelInterface => {
    const result: Record<string, string> = {}
    Object.entries(description ?? {}).forEach(([lang, text]) => { result[lang] = transform(String(text ?? '')) })
    return result as i18nLabelInterface
}

const withModifierPrefix = (modifier: string, description: i18nLabelInterface): i18nLabelInterface =>
    mapLanguages(description, (text) => {
        const trimmed = text.trim()
        if (trimmed.startsWith(modifier)) return trimmed
        return `${modifier} ${trimmed}`.trim()
    })

/** The catalog writes temperatures as "4000K"; the ERP description comes as "4000 K". */
const withoutSpaceBeforeKelvin = (description: i18nLabelInterface): i18nLabelInterface =>
    mapLanguages(description, (text) => text.replace(/(\d)\s+K$/, '$1K'))

const finishOptionFields = (variation: Variation, modifier: string, display: TemporalFinishDisplay) => {
    const description = withModifierPrefix(modifier, display.description ?? variation.getDescriptions())
    const fields: Partial<TemporalOptionEntry> = { description }
    const hex = display.hex ?? variation.getProps().value
    if (hex) fields.hex = String(hex)
    if (display.ral) fields.ral = display.ral
    if (display.rgb) fields.rgb = display.rgb
    if (display.familyName) fields.familyName = display.familyName
    if (display.thumbnailImage) fields.thumbnailImage = display.thumbnailImage
    if (display.thumbnailImageSquare) fields.thumbnailImageSquare = display.thumbnailImageSquare
    return fields
}

/** The byFamily-shaped `attributeOptions` entry of a temporal variation. */
export const toTemporalOption = (variation: Variation, display: TemporalFinishDisplay = {}): TemporalOptionEntry => {
    const props = variation.getProps()
    const modifier = String(variation.getModifier(true))
    const availability = props.availability ?? { since: null }

    let fromValidityDate = ''
    let toValidityDate = ''
    if (isValidTimestamp(availability.since)) fromValidityDate = toDayMonthYear(availability.since)
    if (isValidTimestamp(availability.until)) toValidityDate = toDayMonthYear(availability.until)

    const entry: TemporalOptionEntry = {
        modifier,
        description: display.description ?? variation.getDescriptions(),
        type: TEMPORAL_OPTION_TYPE,
        MOQ: Number(props.moq ?? 0),
        fromValidityDate,
        toValidityDate,
        temporal: temporalValidity(variation),
    }

    if (variation.isType('color')) return { ...entry, ...finishOptionFields(variation, modifier, display) }
    if (variation.isType('installation')) return { ...entry, trackSystem: false }
    if (variation.isType('temperature')) return { ...entry, description: withoutSpaceBeforeKelvin(entry.description) }
    return entry
}

export const toTemporalOffer = (variation: Variation, display: TemporalFinishDisplay = {}): TemporalVariationOffer => ({
    model: String(variation.getProps().model),
    optionsKey: OPTIONS_KEY_BY_TYPE[variation.getType()],
    variation: variation.getProps(),
    option: toTemporalOption(variation, display),
})

/**
 * The /v2/articles/variations payload (`{ variations, base, prices }`) with the temporal variations appended.
 * A temporal variation whose model, type and code the catalog already has is skipped: the catalog one wins.
 */
export const mergeTemporalVariationsIntoCollectionData = <T extends CollectionData>(data: T, temporal: VariationProps[]): T => {
    if (!data || !Array.isArray(temporal) || temporal.length === 0) return data
    const current = Array.isArray(data.variations) ? data.variations : []
    const known = new Set(current.map(keyOfProps))
    const added: VariationProps[] = []
    for (const props of temporal) {
        const key = keyOfProps(props)
        if (known.has(key)) continue
        known.add(key)
        added.push(props)
    }
    if (added.length === 0) return data
    return { ...data, variations: [...current, ...added] }
}

const keyOfVariation = (variation: Variation) =>
    variationKey(String(variation.props?.model), variation.getType(), String(variation.getModifier()))

/**
 * A new ArticleCollection with the temporal variations superposed on `collection`, for one request of one user.
 * The given collection is left as it is (it may be shared between users); base articles and prices are shared,
 * read-only, with it. A temporal variation the catalog already has (same model, type and code) is skipped.
 */
export const superposeTemporalVariations = (collection: ArticleCollection, temporal: VariationProps[]): ArticleCollection => {
    if (!collection || !Array.isArray(temporal) || temporal.length === 0) return collection
    const current = Array.isArray(collection.props) ? collection.props : []
    const known = new Set(current.map(keyOfVariation))
    const added: Variation[] = []
    for (const props of temporal) {
        const variation = new Variation(props)
        const key = keyOfVariation(variation)
        if (known.has(key)) continue
        known.add(key)
        added.push(variation)
    }
    if (added.length === 0) return collection
    const result = new ArticleCollection([...current, ...added])
    result.baseArticles = collection.baseArticles
    result.basePrices = collection.basePrices
    return result
}

/**
 * A byFamily model entry with the temporal options of that model appended to its `attributeOptions` lists
 * (finishes, temperatures, controls, installations). Codes the model already offers are skipped.
 */
export const mergeTemporalOptionsIntoModelData = <T extends ModelData>(modelData: T, offers: TemporalVariationOffer[]): T => {
    if (!modelData || !Array.isArray(offers) || offers.length === 0) return modelData
    const model = String(modelData.model ?? '')
    const own = offers.filter((offer) => offer.model === model)
    if (own.length === 0) return modelData

    const result: ModelData = { ...modelData }
    let added = false
    for (const offer of own) {
        const list = Array.isArray(result[offer.optionsKey]) ? [...(result[offer.optionsKey] as Array<{ modifier?: string }>)] : []
        if (list.some((entry) => String(entry?.modifier) === offer.option.modifier)) continue
        list.push(offer.option)
        result[offer.optionsKey] = list
        added = true
    }
    if (!added) return modelData
    return result as T
}
